import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../../services/apiService';

export interface CreditBalance {
  totalCredits: number;
  availableCredits: number;
  reservedCredits: number;
  monthlyFreeCredits: number;
  creditsUsedThisMonth: number;
  lifetimeCreditsEarned: number;
  lifetimeCreditsSpent: number;
  lastUpdated: string;
  nextMonthlyReset?: string;
  expiringCredits: ExpiringCredit[];
}

export interface ExpiringCredit {
  id: number;
  creditsRemaining: number;
  expiresAt: string;
  daysUntilExpiry: number;
  packageName?: string;
}

export interface CreditTransaction {
  id: string;
  transactionType: 'purchase' | 'usage' | 'bonus' | 'refund' | 'expiry';
  credits: number;
  balanceAfter: number;
  source: string;
  remarks: string;
  createdAt: string;
  featureName?: string;
  amount?: number;
  currency?: string;
}

export interface FeatureAvailability {
  id: number;
  featureKey: string;
  featureName: string;
  description: string;
  requiredCredits: number;
  isPremium: boolean;
  hasAccess: boolean;
  accessReason: string;
  chargingUnit: string;
}

export interface CreditAlert {
  id: string;
  type: 'deduction' | 'insufficient' | 'low_balance' | 'expiry_warning';
  title: string;
  message: string;
  featureName?: string;
  creditsDeducted?: number;
  remainingCredits?: number;
  timestamp: string;
  isRead: boolean;
}

interface CreditsState {
  balance: CreditBalance | null;
  transactions: CreditTransaction[];
  availableFeatures: FeatureAvailability[];
  alerts: CreditAlert[];
  isLoading: boolean;
  error: string | null;
  lastBalanceUpdate: string | null;
}

const BALANCE_STORAGE_KEY = 'persisted_credit_balance';

const loadPersistedBalance = (): CreditBalance | null => {
  try {
    const raw = localStorage.getItem(BALANCE_STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch { }
  return null;
};

const savePersistedBalance = (balance: CreditBalance | null): void => {
  try {
    if (balance) {
      localStorage.setItem(BALANCE_STORAGE_KEY, JSON.stringify(balance));
    } else {
      localStorage.removeItem(BALANCE_STORAGE_KEY);
    }
  } catch { }
};

const initialState: CreditsState = {
  balance: loadPersistedBalance(),
  transactions: [],
  availableFeatures: [],
  alerts: [],
  isLoading: false,
  error: null,
  lastBalanceUpdate: null,
};

// Helper: check if user has a valid auth token (avoids circular store import)
const hasAuthToken = (): boolean => {
  const token =  localStorage.getItem('token') || '';
  return !!token;
};

// Async thunks for API calls
export const fetchCreditBalance = createAsyncThunk(
  'credits/fetchBalance',
  async (_, { getState }) => {
    const state: any = getState();
    return state.credits?.balance || null;
  }
);

export const fetchCreditTransactions = createAsyncThunk(
  'credits/fetchTransactions',
  async (params: { page?: number; limit?: number } = {}, { rejectWithValue }) => {
    if (!hasAuthToken()) {
      return rejectWithValue('Not authenticated');
    }
    try {
      const response: any = await apiService.getTransactionHistory(
        params.page || 1,
        params.limit || 20
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transactions');
    }
  }
);

export const fetchAvailableFeatures = createAsyncThunk(
  'credits/fetchAvailableFeatures',
  async (_, { rejectWithValue }) => {
    if (!hasAuthToken()) {
      return rejectWithValue('Not authenticated');
    }
    try {
      const response: any = await apiService.get('/credits/features/available');
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch available features');
    }
  }
);

export const checkFeatureAccess = createAsyncThunk(
  'credits/checkFeatureAccess',
  async (featureKey: string, { rejectWithValue }) => {
    if (!hasAuthToken()) {
      return rejectWithValue('Not authenticated');
    }
    try {
      const response: any = await apiService.get(`/credits/feature/${featureKey}/check-access`);
      return { featureKey, ...response };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to check feature access');
    }
  }
);

export const useFeature = createAsyncThunk(
  'credits/useFeature',
  async (params: { featureKey: string; usageData?: any }, { rejectWithValue, dispatch }) => {
    if (!hasAuthToken()) {
      return rejectWithValue('Not authenticated');
    }
    try {
      const response: any = await apiService.post(`/credits/feature/${params.featureKey}/use`, params.usageData || {});
      
      const payloadData = response?.data || response || {};
      const deducted = Number(payloadData.credits_deducted || payloadData.creditsDeducted || 0);
      
      // Create success alert
      const alert: CreditAlert = {
        id: `alert_${Date.now()}`,
        type: 'deduction',
        title: 'Credits Deducted',
        message: `${deducted} credits used for ${payloadData.feature?.feature_name || payloadData.feature?.featureName || params.featureKey}`,
        featureName: payloadData.feature?.feature_name || payloadData.feature?.featureName,
        creditsDeducted: deducted,
        remainingCredits: payloadData.remaining_credits || payloadData.remainingCredits || payloadData.available_credits || payloadData.availableCredits,
        timestamp: new Date().toISOString(),
        isRead: false,
      };
      
      dispatch(addAlert(alert));
      
      // Deduct balance locally in store after successful feature usage
      if (deducted > 0) {
        dispatch(deductCredits(deducted));
      }
      
      return response;
    } catch (error: any) {
      const errorData = error.response?.data;
      
      // Create error alert for insufficient credits
      if (error.response?.status === 402 || errorData?.reason === 'insufficient_credits') {
        const alert: CreditAlert = {
          id: `alert_${Date.now()}`,
          type: 'insufficient',
          title: 'Insufficient Credits',
          message: errorData?.message || 'You don\'t have enough credits for this feature',
          featureName: params.featureKey,
          timestamp: new Date().toISOString(),
          isRead: false,
        };
        
        dispatch(addAlert(alert));
      }
      
      return rejectWithValue(errorData?.message || 'Failed to use feature');
    }
  }
);

const creditsSlice = createSlice({
  name: 'credits',
  initialState,
  reducers: {
    addAlert: (state, action: PayloadAction<CreditAlert>) => {
      state.alerts.unshift(action.payload);
      // Keep only last 50 alerts
      if (state.alerts.length > 50) {
        state.alerts = state.alerts.slice(0, 50);
      }
    },
    markAlertAsRead: (state, action: PayloadAction<string>) => {
      const alert = state.alerts.find(a => a.id === action.payload);
      if (alert) {
        alert.isRead = true;
      }
    },
    markAllAlertsAsRead: (state) => {
      state.alerts.forEach(alert => {
        alert.isRead = true;
      });
    },
    removeAlert: (state, action: PayloadAction<string>) => {
      state.alerts = state.alerts.filter(a => a.id !== action.payload);
    },
    clearAlerts: (state) => {
      state.alerts = [];
    },
    updateBalance: (state, action: PayloadAction<Partial<CreditBalance> | number | any>) => {
      const payloadVal = typeof action.payload === 'number'
        ? { availableCredits: action.payload, totalCredits: action.payload }
        : (action.payload || {});
      
      const available = Number(
        payloadVal.credits_balance ??
        payloadVal.availableCredits ??
        payloadVal.available_credits ??
        0
      );
      const spent = Number(
        payloadVal.lifetime_credits_spent ??
        payloadVal.lifetimeCreditsSpent ??
        0
      );
      const earned = Number(
        payloadVal.lifetime_credits_earned ??
        payloadVal.lifetimeCreditsEarned ??
        (available + spent)
      );
      const total = Number(
        payloadVal.totalCredits ??
        payloadVal.total_credits ??
        earned
      );

      if (state.balance) {
        state.balance = {
          ...state.balance,
          ...payloadVal,
          availableCredits: available,
          totalCredits: total,
          lifetimeCreditsSpent: spent || state.balance.lifetimeCreditsSpent,
          lifetimeCreditsEarned: earned || state.balance.lifetimeCreditsEarned,
          creditsUsedThisMonth: spent || state.balance.creditsUsedThisMonth,
        };
      } else {
        state.balance = {
          totalCredits: total,
          availableCredits: available,
          reservedCredits: 0,
          monthlyFreeCredits: 0,
          creditsUsedThisMonth: spent,
          lifetimeCreditsEarned: earned,
          lifetimeCreditsSpent: spent,
          lastUpdated: new Date().toISOString(),
          expiringCredits: [],
          ...payloadVal,
        };
      }
      state.lastBalanceUpdate = new Date().toISOString();
      savePersistedBalance(state.balance);
    },
    addCredits: (state, action: PayloadAction<number>) => {
      const amount = Number(action.payload) || 0;
      if (amount > 0) {
        if (state.balance) {
          state.balance.availableCredits += amount;
          state.balance.totalCredits += amount;
          state.balance.lifetimeCreditsEarned += amount;
        } else {
          state.balance = {
            totalCredits: amount,
            availableCredits: amount,
            reservedCredits: 0,
            monthlyFreeCredits: 0,
            creditsUsedThisMonth: 0,
            lifetimeCreditsEarned: amount,
            lifetimeCreditsSpent: 0,
            lastUpdated: new Date().toISOString(),
            expiringCredits: [],
          };
        }
        state.lastBalanceUpdate = new Date().toISOString();
        savePersistedBalance(state.balance);
      }
    },
    deductCredits: (state, action: PayloadAction<number>) => {
      const amount = Number(action.payload) || 0;
      if (amount > 0) {
        if (!state.balance) {
          state.balance = loadPersistedBalance();
        }
        if (state.balance) {
          state.balance.availableCredits = Math.max(0, state.balance.availableCredits - amount);
          state.balance.lifetimeCreditsSpent = (state.balance.lifetimeCreditsSpent || 0) + amount;
          state.balance.creditsUsedThisMonth = (state.balance.creditsUsedThisMonth || 0) + amount;
          state.lastBalanceUpdate = new Date().toISOString();
          savePersistedBalance(state.balance);
        }
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    checkLowBalance: (state) => {
      if (state.balance && state.balance.availableCredits <= 10 && state.balance.availableCredits > 0) {
        const existingAlert = state.alerts.find(a => a.type === 'low_balance' && !a.isRead);
        if (!existingAlert) {
          const alert: CreditAlert = {
            id: `low_balance_${Date.now()}`,
            type: 'low_balance',
            title: 'Low Credit Balance',
            message: `You have only ${state.balance.availableCredits} credits remaining. Consider purchasing more credits.`,
            remainingCredits: state.balance.availableCredits,
            timestamp: new Date().toISOString(),
            isRead: false,
          };
          state.alerts.unshift(alert);
        }
      }
    },
    checkExpiringCredits: (state) => {
      if (state.balance?.expiringCredits) {
        const soonExpiring = state.balance.expiringCredits.filter(c => c.daysUntilExpiry <= 7);
        
        soonExpiring.forEach(credit => {
          const existingAlert = state.alerts.find(a => 
            a.type === 'expiry_warning' && 
            a.message.includes(credit.id.toString()) && 
            !a.isRead
          );
          
          if (!existingAlert) {
            const alert: CreditAlert = {
              id: `expiry_${credit.id}_${Date.now()}`,
              type: 'expiry_warning',
              title: 'Credits Expiring Soon',
              message: `${credit.creditsRemaining} credits will expire in ${credit.daysUntilExpiry} days`,
              timestamp: new Date().toISOString(),
              isRead: false,
            };
            state.alerts.unshift(alert);
          }
        });
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Balance
      .addCase(fetchCreditBalance.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchCreditBalance.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchCreditBalance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Transactions
      .addCase(fetchCreditTransactions.fulfilled, (state, action) => {
        const payloadData = action.payload?.data || action.payload || {};
        state.transactions = payloadData.transactions || [];
      })
      .addCase(fetchCreditTransactions.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Fetch Available Features
      .addCase(fetchAvailableFeatures.fulfilled, (state, action) => {
        const payloadData = action.payload?.data || action.payload || {};
        const features = Array.isArray(payloadData) ? payloadData : (payloadData.features || []);
        state.availableFeatures = features.map((f: any) => ({
          id: f.id,
          featureKey: f.feature_key ?? f.featureKey ?? '',
          featureName: f.feature_name ?? f.featureName ?? '',
          description: f.description ?? '',
          requiredCredits: f.required_credits ?? f.requiredCredits ?? f.cost_per_use ?? f.price ?? 0,
          isPremium: f.is_premium === 1 || f.is_premium === true || f.isPremium === true,
          hasAccess: f.has_access ?? f.hasAccess ?? true,
          accessReason: f.access_reason ?? f.accessReason ?? '',
          chargingUnit: f.charging_unit ?? f.chargingUnit ?? 'use',
        }));
      })
      .addCase(fetchAvailableFeatures.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Use Feature
      .addCase(useFeature.fulfilled, () => {
      })
      .addCase(useFeature.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  addAlert,
  markAlertAsRead,
  markAllAlertsAsRead,
  removeAlert,
  clearAlerts,
  updateBalance,
  addCredits,
  deductCredits,
  clearError,
  checkLowBalance,
  checkExpiringCredits,
} = creditsSlice.actions;

export default creditsSlice.reducer;